import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {CooldownManager} from "./cooldown-manager";
import {DialogEventEmitter} from "./event-emitter";
import {DialogRegistry} from "./dialog-registry";
import {FlowLockInput, FlowManager} from "./flow-manager";
import {LifecycleManager} from "./lifecycle-manager";
import {PolicyEngine} from "./policy-engine";
import {buildRequest, QueueManager} from "./queue-manager";
import {ZIndexManager} from "./z-index-manager";
import {
    ContextProvider,
    DialogConfig,
    DialogEventListener,
    DialogEventType,
    DialogRequest,
    DialogResult,
    OpenOptions,
    OrchestratorContext,
    RenderSink,
    VisibleDialog,
} from "./types";

interface ActiveEntry {
    request: DialogRequest;
    config: DialogConfig;
    zIndex: number;
    focused: boolean;
}

interface PendingPredicate {
    predicate: (ctx: OrchestratorContext) => boolean;
    resolve: () => void;
    reject: (err: Error) => void;
    timer?: ReturnType<typeof setTimeout>;
}

const DEFAULT_CONTEXT: OrchestratorContext = {
    route: "/",
    isAuthenticated: false,
    activeFlow: null,
    activeDialogs: [],
    isMobile: false,
    isPwaInstalled: false,
    hasPendingDeposit: false,
};

/**
 * The conductor. It is framework-agnostic (no React / no Redux imports) and is
 * the single place where *actions* happen — opening, closing, queueing,
 * dependency resolution, flow locking. Everything else (registry, policy,
 * cooldowns, queue, z-index, flows, lifecycle) is a collaborator it composes.
 *
 * Data flow:
 *   host → setContextProvider() (Redux and router → engine, input)
 *   host → setRenderSink() (engine → Redux mirror, output)
 *   app → open()/close()/lockFlow() (imperative API)
 *   engine → reconcile() (the decision loop)
 */
export class DialogOrchestrator {
    readonly registry: DialogRegistry;
    readonly queue: QueueManager;
    readonly policy: PolicyEngine;
    readonly cooldowns: CooldownManager;
    readonly flows: FlowManager;
    readonly zIndex: ZIndexManager;
    readonly lifecycle: LifecycleManager;
    readonly events: DialogEventEmitter;

    private active = new Map<string, ActiveEntry>();
    private resolvers = new Map<string, (result: DialogResult) => void>();
    private predicates = new Set<PendingPredicate>();

    private contextProvider: ContextProvider = () => DEFAULT_CONTEXT;
    private renderSink: RenderSink = () => {};

    private wakeupTimer: ReturnType<typeof setTimeout> | null = null;
    private reconciling = false;
    private reconcileQueued = false;

    constructor(configs: DialogConfig[] = [], deps?: Partial<{
        registry: DialogRegistry;
        cooldowns: CooldownManager;
        flows: FlowManager;
        zIndex: ZIndexManager;
        events: DialogEventEmitter;
    }>) {
        this.registry = deps?.registry ?? new DialogRegistry(configs);
        this.cooldowns = deps?.cooldowns ?? new CooldownManager();
        this.flows = deps?.flows ?? new FlowManager();
        this.zIndex = deps?.zIndex ?? new ZIndexManager();
        this.events = deps?.events ?? new DialogEventEmitter();
        this.queue = new QueueManager();
        this.policy = new PolicyEngine(this.registry, this.cooldowns, this.flows);
        this.lifecycle = new LifecycleManager(this.events);
    }

    // ── wiring ──────────────────────────────────────────────────────────────

    setContextProvider(provider: ContextProvider): void {
        this.contextProvider = provider;
    }

    setRenderSink(sink: RenderSink): void {
        this.renderSink = sink;
        sink(this.visibleDialogs());
    }

    on(listener: DialogEventListener): () => void;
    on(type: DialogEventType, listener: DialogEventListener): () => void;
    on(a: DialogEventType | DialogEventListener, b?: DialogEventListener): () => void {
        // overload passthrough to the emitter
        return typeof a === "function" ? this.events.on(a) : this.events.on(a, b!);
    }

    // ── public imperative API ────────────────────────────────────────────────

    /**
     * Request a dialog. Returns the instanceId plus a promise that resolves when
     * the dialog finally closes (with its result), enabling async flows like
     * `const res = await orchestrator.open(WALLET).result`.
     */
    open(id: DialogsEnum, opts: OpenOptions = {}): {instanceId: string; result: Promise<DialogResult>} {
        const now = Date.now();
        const config = this.registry.get(id);

        // de-duplicate identical requests if configured
        if (config.dedupe && (this.queue.hasId(id) || this.hasActiveId(id))) {
            const existing = this.findInstanceId(id);
            return {
                instanceId: existing ?? opts.instanceId ?? id,
                result: existing ? this.resultPromise(existing) : Promise.resolve(failed(id, existing ?? id)),
            };
        }

        const request = buildRequest(id, opts, now);
        const ctx = this.getContext();
        this.lifecycle.emit("requested", ctx, request);
        this.queue.enqueue(request);
        this.lifecycle.transition(request, "queued", ctx);

        const result = new Promise<DialogResult>((resolve) => {
            this.resolvers.set(request.instanceId, resolve);
        });

        void this.reconcile();
        return {instanceId: request.instanceId, result};
    }

    /** Close a visible dialog (or cancel a queued one). */
    close(instanceId: string, result?: Partial<DialogResult>): void {
        const entry = this.active.get(instanceId);
        if (entry) {
            void this.finalizeClose(entry, {
                id: entry.request.id,
                instanceId,
                success: result?.success ?? false,
                reason: result?.reason ?? "user",
                data: result?.data,
            });
            return;
        }
        // not visible yet — cancel from the queue
        this.cancel(instanceId, result?.reason ?? "cancelled");
    }

    /** Convenience: close with a successful result + optional payload. */
    resolve(instanceId: string, data?: unknown): void {
        this.close(instanceId, {success: true, reason: "programmatic", data});
    }

    cancel(instanceId: string, reason: DialogResult["reason"] = "cancelled"): void {
        const queued = this.queue.remove(instanceId);
        if (queued) {
            const ctx = this.getContext();
            this.lifecycle.transition(queued, "cancelled", ctx);
            this.settle({id: queued.id, instanceId, success: false, reason});
            return;
        }
        const entry = this.active.get(instanceId);
        if (entry) this.close(instanceId, {success: false, reason});
    }

    cancelById(id: DialogsEnum): void {
        for (const r of this.queue.cancelById(id)) {
            this.settle({id, instanceId: r.instanceId, success: false, reason: "cancelled"});
        }
        for (const [instanceId, entry] of this.active) {
            if (entry.request.id === id) this.close(instanceId, {success: false, reason: "cancelled"});
        }
    }

    // ── flows ─────────────────────────────────────────────────────────────────

    lockFlow(flow: string | FlowLockInput): void {
        const input = typeof flow === "string" ? {name: flow} : flow;
        this.flows.lock(input);
        this.lifecycle.emit("flow-locked", this.getContext(), undefined, undefined, input.name);
        void this.reconcile();
    }

    unlockFlow(name: string): void {
        this.flows.unlock(name);
        this.lifecycle.emit("flow-unlocked", this.getContext(), undefined, undefined, name);
        void this.reconcile();
    }

    // ── async orchestration ────────────────────────────────────────────────────

    /**
     * Resolve when context satisfies `predicate` (re-checked on every
     * `notifyContextChanged`). Rejects on timeout. Useful for "wait for auth",
     * "wait for route change", "wait for deposit completion".
     */
    waitFor(predicate: (ctx: OrchestratorContext) => boolean, timeoutMs = 30_000): Promise<void> {
        if (predicate(this.getContext())) return Promise.resolve();
        return new Promise<void>((resolve, reject) => {
            const entry: PendingPredicate = {predicate, resolve, reject};
            if (timeoutMs > 0) {
                entry.timer = setTimeout(() => {
                    this.predicates.delete(entry);
                    reject(new Error("waitFor timed out"));
                }, timeoutMs);
            }
            this.predicates.add(entry);
        });
    }

    // ── context change entrypoints (called by the host) ─────────────────────────

    /** Host calls this whenever Redux/router state changes. */
    notifyContextChanged(): void {
        const ctx = this.getContext();
        for (const p of [...this.predicates]) {
            if (p.predicate(ctx)) {
                if (p.timer) clearTimeout(p.timer);
                this.predicates.delete(p);
                p.resolve();
            }
        }
        void this.reconcile();
    }

    /** Host calls this on navigation; closes non-persistent dialogs. */
    handleRouteChange(): void {
        for (const [instanceId, entry] of [...this.active]) {
            if (!entry.config.persistent) {
                this.close(instanceId, {success: false, reason: "programmatic"});
            }
        }
        // re-read predicates against the new route + re-evaluate the queue.
        this.notifyContextChanged();
    }

    // ── introspection ───────────────────────────────────────────────────────────

    visibleDialogs(): VisibleDialog[] {
        return [...this.active.values()]
            .sort((a, b) => a.zIndex - b.zIndex)
            .map((e) => ({
                id: e.request.id,
                instanceId: e.request.instanceId,
                zIndex: e.zIndex,
                props: e.request.props,
                blocking: !!e.config.blocking,
                focused: e.focused,
            }));
    }

    isOpen(id: DialogsEnum): boolean {
        return this.hasActiveId(id);
    }

    /**
     * Live props for a visible instance. Props (which may contain callbacks) are
     * kept here rather than in Redux so the store stays serialisable — the React
     * host reads them by instanceId at render time.
     */
    getProps(instanceId: string): Record<string, unknown> | undefined {
        return this.active.get(instanceId)?.request.props;
    }

    /** Whether this instance is still visible in the engine (the Redux mirror can lag a microtask). */
    isActive(instanceId: string): boolean {
        return this.active.has(instanceId);
    }

    // ── the decision loop ────────────────────────────────────────────────────────

    /**
     * Idempotent, re-entrant-safe reconciliation. Because admission can `await`
     * `onBeforeOpen`, a context change mid-flight just flips a dirty flag and the
     * loop re-runs once it settles — never overlapping.
     */
    private async reconcile(): Promise<void> {
        if (this.reconciling) {
            this.reconcileQueued = true;
            return;
        }
        this.reconciling = true;
        try {
            do {
                this.reconcileQueued = false;
                await this.runReconcilePass();
            } while (this.reconcileQueued);
        } finally {
            this.reconciling = false;
            this.scheduleWakeup();
        }
    }

    private async runReconcilePass(): Promise<void> {
        const now = Date.now();
        let ctx = this.getContext();

        // 1) close active dialogs that became invalid for the current context.
        this.pruneActive(ctx);
        ctx = this.getContext();

        // 2) walk eligible requests by context-aware priority.
        const ready = this.queue.ready(now, (r) => this.policy.priority(r, ctx));

        for (const request of ready) {
            if (!this.queue.has(request.instanceId)) continue; // removed mid-pass
            const decision = this.policy.evaluate(request, ctx);

            if (decision.kind === "reject") {
                this.queue.remove(request.instanceId);
                this.lifecycle.transition(request, "cancelled", ctx);
                this.lifecycle.emit("blocked", ctx, request);
                this.settle({id: request.id, instanceId: request.instanceId, success: false, reason: "cancelled"});
                continue;
            }

            if (decision.kind === "wait") {
                this.lifecycle.transition(request, "waiting-dependency", ctx);
                if (decision.dependencies?.length) {
                    this.ensureDependencies(request, decision.dependencies);
                }
                this.lifecycle.emit("blocked", ctx, request);
                continue;
            }

            // decision.kind === "admit": respect mode + blocking before opening.
            if (!this.canPresentNow(request, ctx)) continue;

            await this.present(request, ctx);
            ctx = this.getContext(); // active set changed; refresh for next item
        }
    }

    /** Whether the request can be shown given current active dialogs + its mode. */
    private canPresentNow(request: DialogRequest, ctx: OrchestratorContext): boolean {
        const config = this.registry.get(request.id);
        const mode = request.mode ?? config.mode ?? "queue";

        if (mode === "replace") return true;     // it will clear what's below
        if (mode === "stack") return true;        // z-index handles visual order

        // queue: wait until no blocking dialog is on screen.
        const blocked = [...this.active.values()].some((e) => e.config.blocking);
        if (blocked) {
            this.lifecycle.emit("blocked", ctx, request);
            return false;
        }
        return true;
    }

    /** Open a request: run hooks, allocate z-index, mark active, mirror, focus. */
    private async present(request: DialogRequest, ctx: OrchestratorContext): Promise<void> {
        const config = this.registry.get(request.id);
        const mode = request.mode ?? config.mode ?? "queue";

        if (mode === "replace") {
            for (const [instanceId, entry] of [...this.active]) {
                await this.finalizeClose(entry, {
                    id: entry.request.id,
                    instanceId,
                    success: false,
                    reason: "replaced",
                });
            }
        }

        this.queue.remove(request.instanceId);
        this.lifecycle.transition(request, "opening", ctx);

        try {
            await config.onBeforeOpen?.(request, ctx);
        } catch (err) {

            console.error(`[dialog-orchestrator] onBeforeOpen failed for ${request.id}`, err);
            this.lifecycle.transition(request, "cancelled", ctx);
            this.settle({id: request.id, instanceId: request.instanceId, success: false, reason: "cancelled"});
            return;
        }

        const zIndex = this.zIndex.allocate(config, this.visibleDialogs());
        this.active.set(request.instanceId, {request, config, zIndex, focused: false});
        this.cooldowns.registerDisplay(request.id);
        this.lifecycle.transition(request, "open", this.getContext());
        this.refreshFocusAndRender();
    }

    /** Tear down an active dialog and run its after-close hook. */
    private async finalizeClose(entry: ActiveEntry, result: DialogResult): Promise<void> {
        const {request, config} = entry;
        const ctxBefore = this.getContext();

        this.lifecycle.transition(request, result.reason === "replaced" ? "replaced" : "closing", ctxBefore);
        this.active.delete(request.instanceId);
        this.lifecycle.transition(request, "closed", this.getContext(), result);
        this.refreshFocusAndRender();

        try {
            await config.onAfterClose?.(result, this.getContext());
        } catch (err) {

            console.error(`[dialog-orchestrator] onAfterClose failed for ${request.id}`, err);
        }

        this.settle(result);
        void this.reconcile(); // closing may unblock queued dialogs
    }

    // ── dependency resolution ─────────────────────────────────────────────────────

    /**
     * Make sure each unmet dependency is scheduled. If a dependency was already
     * attempted but is now gone *and still unmet*, the dependency failed (e.g.
     * user cancelled AUTH) → cancel the dependent request.
     */
    private ensureDependencies(request: DialogRequest, deps: DialogsEnum[]): void {
        const attempted: DialogsEnum[] = (request.meta?.depsRequested as DialogsEnum[]) ?? [];

        for (const dep of deps) {
            const inFlight = this.hasActiveId(dep) || this.queue.hasId(dep);
            if (inFlight) continue;

            if (attempted.includes(dep)) {
                // we already ran it and the dependency is still unmet → failed
                this.cancel(request.instanceId, "dependency-failed");
                return;
            }

            // schedule the prerequisite ahead of the dependent
            this.open(dep, {source: "dependency"});
            attempted.push(dep);
        }

        request.meta = {...request.meta, depsRequested: attempted};
    }

    // ── helpers ─────────────────────────────────────────────────────────────────────

    private pruneActive(ctx: OrchestratorContext): void {
        for (const [instanceId, entry] of [...this.active]) {
            if (entry.config.persistent) continue;
            const decision = this.policy.evaluate(entry.request, ctx);
            const routeGone = decision.kind === "wait" &&
                (decision.reason === "forbidden-route" || decision.reason === "route-not-allowed");
            if (routeGone) {
                this.close(instanceId, {success: false, reason: "programmatic"});
            }
        }
    }

    private refreshFocusAndRender(): void {
        const sorted = [...this.active.values()].sort((a, b) => a.zIndex - b.zIndex);
        const top = sorted[sorted.length - 1];
        for (const entry of sorted) {
            const shouldFocus = entry === top;
            if (entry.focused !== shouldFocus) {
                entry.focused = shouldFocus;
                if (shouldFocus) this.lifecycle.emit("focused", this.getContext(), entry.request);
                else this.lifecycle.emit("hidden", this.getContext(), entry.request);
            }
        }
        this.renderSink(this.visibleDialogs());
    }

    private scheduleWakeup(): void {
        if (this.wakeupTimer) clearTimeout(this.wakeupTimer);
        const next = this.queue.nextWakeupAt(Date.now());
        if (next == null) return;
        const delay = Math.max(0, next - Date.now());
        this.wakeupTimer = setTimeout(() => {
            this.wakeupTimer = null;
            void this.reconcile();
        }, delay);
    }

    /** Build context and override activeDialogs with engine truth (z-order). */
    private getContext(): OrchestratorContext {
        const base = this.contextProvider();
        return {
            ...base,
            activeDialogs: this.visibleDialogs().map((v) => v.id),
        };
    }

    private hasActiveId(id: DialogsEnum): boolean {
        for (const e of this.active.values()) if (e.request.id === id) return true;
        return false;
    }

    private findInstanceId(id: DialogsEnum): string | undefined {
        for (const e of this.active.values()) if (e.request.id === id) return e.request.instanceId;
        return this.queue.snapshot().find((r) => r.id === id)?.instanceId;
    }

    private resultPromise(instanceId: string): Promise<DialogResult> {
        return new Promise<DialogResult>((resolve) => {
            const prev = this.resolvers.get(instanceId);
            this.resolvers.set(instanceId, (r) => {
                prev?.(r);
                resolve(r);
            });
        });
    }

    private settle(result: DialogResult): void {
        const resolve = this.resolvers.get(result.instanceId);
        if (resolve) {
            this.resolvers.delete(result.instanceId);
            resolve(result);
        }
    }
}

function failed(id: DialogsEnum, instanceId: string): DialogResult {
    return {id, instanceId, success: false, reason: "cancelled"};
}
